import React from "react";
import { Grid, Typography } from "@mui/material";
import DetailRow from "../../pages/common-features/DetailRow";
import { useSelector } from "react-redux";
import { RootState } from "../../services/redux/store";

interface LoanDetailsTabProps {
  loanDetails: any[];
  isMobile: boolean;
  pageType?: string;
}

const LoanDetailsTab: React.FC<LoanDetailsTabProps> = ({
  loanDetails,
  isMobile,
  pageType = "DEFAULT",
}) => {
  const { fetchUserApplicationData } = useSelector(
    (state: RootState) => state.stepper
  );

  // Helper to map fields from the vertical step to the required format
  function mapLoanDetailsFromVerticalStep(verticalStep: any) {
    if (!verticalStep || !verticalStep?.fields) return [];
    // Only pick LoanDetailsLoanAmount and collateral fields
    return verticalStep?.fields
      .filter(
        (fieldObj: any) =>
          Object.keys(fieldObj)[0] === "LoanDetailsLoanAmount" ||
          Object.keys(fieldObj)[0] === "collateral" ||
          Object.keys(fieldObj)[0] === "collateralType" ||
          Object.keys(fieldObj)[0] === "collateralAmount"
      )
      .map((fieldObj: any) => {
        const name = Object.keys(fieldObj)?.[0];
        const value = fieldObj?.[name];
        // Format label: Loan Amount, Collateral
        const label =
          name === "LoanDetailsLoanAmount"
            ? "Loan Amount"
            : name === "collateralType"
            ? "Collateral Type"
            : name === "collateralAmount"
            ? "Collateral Amount"
            : name.charAt(0).toUpperCase() + name.slice(1);
        return {
          label,
          name,
          value,
        };
      });
  }

  const mergeFieldArrays = (primaryFields: any[] = [], fallbackFields: any[] = []) => {
    const normalizeKey = (field: any) =>
      String(field?.name ?? field?.key ?? field?.label ?? "").toLowerCase();

    const primaryMap = new Map<string, any>();
    primaryFields.forEach((field) => {
      const key = normalizeKey(field);
      if (key) primaryMap.set(key, field);
    });

    const fallbackMap = new Map<string, any>();
    fallbackFields.forEach((field) => {
      const key = normalizeKey(field);
      if (key) fallbackMap.set(key, field);
    });

    const merged = fallbackFields.map((fallbackField) => {
      const key = normalizeKey(fallbackField);
      const primaryField = key ? primaryMap.get(key) : undefined;
      return primaryField
        ? { ...fallbackField, ...primaryField, value: primaryField?.value ?? "" }
        : { ...fallbackField, value: fallbackField?.value ?? "" };
    });

    primaryFields.forEach((primaryField) => {
      const key = normalizeKey(primaryField);
      if (!key || fallbackMap.has(key)) return;
      merged.push({ ...primaryField, value: primaryField?.value ?? "" });
    });

    return merged;
  };

  let loanDetailsToShow = loanDetails;

  if (
    pageType === "SUMMARY" &&
    fetchUserApplicationData &&
    !fetchUserApplicationData?.partnerStartedJourney
  ) {
    // Find the 'Applicant & Loan Details' horizontal step
    const applicantLoanStep = fetchUserApplicationData?.horizontalSteps?.find(
      (h: any) => h.horizontalStepName === "Applicant & Loan Details"
    );
    // Find the old 'Loan Details' / new 'Co-Applicant Details' vertical step
    const loanDetailsStep = applicantLoanStep?.verticalSteps?.find(
      (v: any) =>
        Number(v?.verticalStepId) === 9 ||
        v.verticalStepName === "Loan Details" ||
        v.verticalStepName === "Co-Applicant Details"
    );
    const fallbackLoanDetails = loanDetails || [];
    if (loanDetailsStep) {
      const mappedLoanDetails = mapLoanDetailsFromVerticalStep(loanDetailsStep);
      loanDetailsToShow = mergeFieldArrays(mappedLoanDetails, fallbackLoanDetails);
    } else {
      loanDetailsToShow = mergeFieldArrays([], fallbackLoanDetails);
    }
  }

  if (!loanDetailsToShow || loanDetailsToShow.length === 0) {
    return (
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary">
          No details available.
        </Typography>
      </Grid>
    );
  }

  return (
    <>
      {loanDetailsToShow?.map((field: any, subIndex: number) => {
        const isLoanAmount =
          field?.label?.toLowerCase() === "loan amount".toLowerCase() ||
          field?.name?.toLowerCase() === "loandetailsloanamount".toLowerCase();

        const updatedValue = isLoanAmount
          ? `₹ ${field?.value ?? ""}`
          : field?.value ?? "";

        return (
          <Grid item xs={12} sm={6} md={3} key={subIndex}>
            <DetailRow
              item={{
                label: field?.label ?? "",
                value: updatedValue,
                isVerified: false,
              }}
            />
          </Grid>
        );
      })}
    </>
  );
};

export default LoanDetailsTab;
